import React, { useState, useEffect, useContext, useRef } from 'react';
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import Button from "components/CustomButtons/Button.js";
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import AlertDialog from '../components/AlertDialog';
import { api, FirebaseContext } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import {
  Select,
  MenuItem,
  Grid,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import { colors } from '../components/Theme/WebTheme';
import { MAIN_COLOR } from "../common/sharedFunctions";

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
    width: 192,
    height: 192
  },
  container1: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.Header_Text,
    borderRadius: '19px',
    padding: '30px',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  title: {
    color: colors.Header_Text,
    textAlign: 'center',
    backgroundColor: colors.Header,
    width: '9rem',
    position: 'absolute',
    top: 0,
    paddingBottom: 5,
    borderBottomRightRadius: '15px',
    borderBottomLeftRadius: '15px'
  },
  img: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
    backgroundColor: colors.Header,
    alignContent: 'center',
    borderRadius: '19px',
    padding: '10px'
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  buttonStyle: {
    margin: 0,
    width: '100%',
    height: 40,
    borderRadius: '30px',
    backgroundColor: MAIN_COLOR,
    color: colors.WHITE,
    marginTop: 16
  },
  rootRtl: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20
    }
  },
  rootRtl3: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 20
    },
    "& legend": {
      textAlign: "right",
      marginRight: 20
    }
  },
  rootRtl_1: {
    "& label": {
      right: 10,
      left: "auto",
      paddingRight: 33
    },
    "& legend": {
      textAlign: "right",
      marginRight: 25
    }
  },
  selectField: {
    color: "black",
  },
}));

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const MyProfile = () => {
  const { authRef, RecaptchaVerifier, unlink, linkWithPhoneNumber } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    updateProfile,
    updateWebProfileImage,
    updateProfileWithEmail,
    checkUserExists,
    requestMobileOtp,
    updateAuthMobile
  } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });

  const languagedata = useSelector(state => state.languagedata);
  const [langSelection, setLangSelection] = useState(0);
  const [multiLanguage, setMultiLanguage] = useState();

  const fileInput = useRef();
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpModal, setOtpModal] = useState(false);
  const [savePressed, setSavePressed] = useState(false);
  const [verificationResult, setVerificationResult] = useState();

  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    usertype: '',
    verifyId: ''
  });

  useEffect(() => {
    if (languagedata.langlist) {
      let arr = Object.keys(languagedata.langlist);
      let selected = -1;
      for (let i = 0; i < arr.length; i++) {
        if (auth.profile && auth.profile.lang && auth.profile.lang.langLocale && auth.profile.lang.langLocale === languagedata.langlist[arr[i]].langLocale) {
          selected = i;
        }
      }
      // with no language on the profile, start from the default language
      // rather than whichever happens to be first, or saving switches to it
      if (selected === -1) {
        selected = arr.findIndex((key) => languagedata.langlist[key].default === true);
      }
      if (selected !== -1) {
        setLangSelection(selected);
      }
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist, auth.profile, setMultiLanguage, setLangSelection]);

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setData({
        firstName: auth.profile.firstName,
        lastName: auth.profile.lastName,
        email: auth.profile.email,
        mobile: auth.profile.mobile,
        usertype: auth.profile.usertype,
        profile_image: auth.profile.profile_image ? auth.profile.profile_image : '',
        verifyId: auth.profile.verifyId ? auth.profile.verifyId : ''
      });
      if (savePressed) {
        setLoading(false);
        setCommonAlert({ open: true, msg: t('profile_updated') });
        setSavePressed(false);
      }
    }
  }, [auth.profile, t, savePressed]);

  const updateData = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  }

  const saveProfile = () => {
    setSavePressed(true);
    let arr = Object.keys(languagedata.langlist);
    const lang = multiLanguage[arr[langSelection]];
    localStorage.setItem("lang", JSON.stringify({ langLocale: lang.langLocale, dateLocale: lang.dateLocale }));
    const profileData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      mobile: data.mobile,
      verifyId: data.verifyId,
      lang: { langLocale: lang.langLocale, dateLocale: lang.dateLocale }
    };
    // The database refuses an update holding undefined, so a profile without
    // a mobile (as admins have) lost the whole save while the page still
    // said it was updated. Leave unset fields out instead.
    Object.keys(profileData).forEach((key) => {
      if (profileData[key] === undefined) {
        delete profileData[key];
      }
    });
    dispatch(updateProfile(profileData));
    if (profileImage) {
      updateWebProfileImage(profileImage);
    }
    i18n.addResourceBundle(lang.langLocale, 'translations', lang.keyValuePairs);
    i18n.changeLanguage(lang.langLocale);
    moment.locale(lang.dateLocale);
    setProfileImage(null);
    setCommonAlert({ open: true, msg: t('profile_updated') });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (data.email !== auth.profile.email && data.mobile !== auth.profile.mobile) {
      setCommonAlert({ open: true, msg: t('update_any') });
    } else if (data.firstName && data.firstName.length > 0 && data.lastName && data.lastName.length > 0) {
      if (data.email !== auth.profile.email) {
        if (EMAIL_REGEX.test(data.email)) {
          setLoading(true);
          checkUserExists({ email: data.email }).then((res) => {
            if (res.users && res.users.length > 0) {
              setLoading(false);
              alert(t('user_exists'));
            } else if (res.error) {
              setLoading(false);
              alert(t('email_or_mobile_issue'));
            } else {
              data.uid = auth.profile.uid;
              dispatch(updateProfileWithEmail(data));
              setLoading(false);
              setCommonAlert({ open: true, msg: t('profile_updated') });
            }
          });
        } else {
          setCommonAlert({ open: true, msg: t('proper_email') });
        }
      } else if (data.mobile !== auth.profile.mobile) {
        if (data.mobile && data.mobile.length > 6) {
          setLoading(true);
          checkUserExists({ mobile: data.mobile }).then(async (res) => {
            if (res.users && res.users.length > 0) {
              setLoading(false);
              alert(t('user_exists'));
            } else if (res.error) {
              setLoading(false);
              alert(t('email_or_mobile_issue'));
            } else {
              setLoading(false);
              setOtpModal(true);
              if (settings.customMobileOTP) {
                dispatch(requestMobileOtp(data.mobile));
              } else {
                window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', { size: 'invisible' });
                if (auth.profile.mobile && auth.profile.mobile.length > 6) {
                  await unlink(authRef().currentUser, 'phone');
                }
                const confirmation = await linkWithPhoneNumber(authRef().currentUser, data.mobile, window.recaptchaVerifier);
                setVerificationResult(confirmation);
              }
            }
          });
        } else {
          setCommonAlert({ open: true, msg: t('mobile_no_blank_error') });
        }
      } else {
        setLoading(true);
        saveProfile();
      }
    } else {
      setCommonAlert({ open: true, msg: t('no_details_error') });
    }
  }

  const handleOtpCancel = () => {
    setOtp('');
    setOtpModal(false);
    window.recaptchaVerifier = null;
  }

  const handleVerifyOtp = async () => {
    if (otp && otp.length === 6 && !isNaN(otp)) {
      setLoading(true);
      if (settings.customMobileOTP) {
        const res = await updateAuthMobile(data.mobile, otp);
        if (res.success) {
          saveProfile();
        } else {
          setOtp('');
          setLoading(false);
          setCommonAlert({
            open: true,
            msg: res.error === 'Error updating user' ? t('user_exists') : t('otp_validate_error')
          });
        }
      } else {
        if (verificationResult) {
          verificationResult.confirm(otp).then(() => {
            saveProfile();
          }).catch(() => {
            setOtp('');
            setLoading(false);
            window.recaptchaVerifier.clear();
            setCommonAlert({ open: true, msg: t('otp_validate_error') });
          });
        }
      }
    } else {
      setOtp('');
      setCommonAlert({ open: true, msg: t('otp_validate_error') });
    }
    setOtpModal(false);
    window.recaptchaVerifier = null;
  }

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' })
  };

  const handleLanguageSelect = (event) => {
    setLangSelection(event.target.value);
  };

  return (
    auth.loading ? <CircularLoading /> :
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div id="sign-in-button" />
        <div className={classes.paper}>
          <form className={classes.form} onSubmit={handleSubmit} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            <div className={classes.container1}>
              <Typography component="h1" variant="h5" className={classes.title}>
                {t('profile_title')}
              </Typography>
              <CardContent className={classes.img}>
                <div
                  onClick={() => fileInput.current.click()}
                  style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
                >
                  {profileImage ? (
                    <img src={URL.createObjectURL(profileImage)} alt="Profile" style={{ width: 130, height: 130, borderRadius: 65 }} />
                  ) : data.profile_image ? (
                    <img src={data.profile_image} alt="Profile" style={{ width: 130, height: 130, borderRadius: 65 }} />
                  ) : (
                    <AccountCircleIcon style={{ width: 130, height: 130, color: colors.WHITE }} />
                  )}
                </div>
                <input
                  onChange={(e) => setProfileImage(e.target.files[0])}
                  multiple={false}
                  ref={fileInput}
                  type="file"
                  hidden
                />
              </CardContent>
              <TextField
                className={isRTL === "rtl" ? classes.rootRtl : null}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="firstName"
                label={t('firstname')}
                name="firstName"
                autoComplete="firstName"
                onChange={updateData}
                value={data.firstName}
                autoFocus
              />
              <TextField
                className={isRTL === "rtl" ? classes.rootRtl_1 : null}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="lastName"
                label={t('lastname')}
                name="lastName"
                autoComplete="lastName"
                onChange={updateData}
                value={data.lastName}
              />
              <TextField
                className={isRTL === "rtl" ? classes.rootRtl : null}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('email')}
                name="email"
                autoComplete="email"
                onChange={updateData}
                value={data.email}
              />
              <TextField
                className={isRTL === "rtl" ? classes.rootRtl3 : null}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="mobile"
                label={t('phone')}
                name="mobile"
                autoComplete="mobile"
                onChange={updateData}
                value={data.mobile}
                dir="ltr"
              />
              <TextField
                className={isRTL === "rtl" ? classes.rootRtl : null}
                variant="outlined"
                margin="normal"
                fullWidth
                id="usertype"
                label={t('usertype')}
                name="usertype"
                value={data.usertype}
                disabled={true}
              />
              {((settings && settings.imageIdApproval) || (data && data.verifyId)) && auth.profile.usertype !== 'admin' ?
                <TextField
                  className={isRTL === "rtl" ? classes.rootRtl : null}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  id="verifyId"
                  label={t('verify_id')}
                  name="verifyId"
                  value={data.verifyId}
                  onChange={updateData}
                />
                : null}
              {multiLanguage && Object.keys(multiLanguage).length > 1 ?
                <Select
                  id="language-select"
                  className={classes.selectField}
                  value={langSelection}
                  variant="outlined"
                  onChange={handleLanguageSelect}
                  style={{ backgroundColor: '#fff', width: '100%', marginTop: '16px', }}
                >
                  {
                    Object.keys(multiLanguage).map((key, index) => <MenuItem key={key} value={index}>
                      {multiLanguage[key].langName}
                    </MenuItem>)
                  }
                </Select>
                : null}
              {loading ?
                <Grid
                  container
                  spacing={0}
                  alignItems="center"
                  style={{ minHeight: '3vh', paddingTop: 20, justifyContent: 'center', alignContent: 'center' }}
                >
                  <CircularProgress />
                </Grid>
                :
                <Button
                  type="submit"
                  onClick={handleSubmit}
                  fullWidth
                  variant="contained"
                  color="secondaryButton"
                  className={classes.buttonStyle}
                >
                  {t('submit')}
                </Button>
              }
            </div>
          </form>
        </div>
        <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
        <Dialog open={otpModal} onClose={handleOtpCancel}>
          <DialogTitle>{t('otp_here')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="otp"
              label={t('otp')}
              type="numeric"
              fullWidth
              variant="standard"
              onChange={(e) => setOtp(e.target.value)}
              value={otp}
            />
          </DialogContent>
          {loading ?
            <DialogActions style={{ justifyContent: 'center', alignContent: 'center' }}>
              <CircularProgress />
            </DialogActions>
            :
            <DialogActions>
              <Button onClick={handleOtpCancel}>{t('cancel')}</Button>
              <Button onClick={handleVerifyOtp}>{t('verify_otp')}</Button>
            </DialogActions>
          }
        </Dialog>
      </Container>
  );
};

export default MyProfile;
